import moment from 'moment';
import { formatBar, getBarTime } from './historyProvider';

let prevFrom = {};
let latestTo = {};

export const clearPrevFromParams = () => {
  prevFrom = {};
  latestTo = {};
};

export const getFromToParams = ({
  symbolInfo,
  resolution,
  from,
  to,
  isCurrent,
}) => {
  const isByMinute = ['1', '5', '15', '30'].includes(resolution);
  const isByHour = ['60', '120', '240'].includes(resolution);
  const oldestDate = moment()
    .set('year', 2000)
    .startOf('year')
    .toDate();
  let From = new Date(from * 1000);
  let To = new Date(to * 1000);
  if (
    moment()
      .startOf('day')
      .unix() ===
    moment(To)
      .startOf('day')
      .unix()
  ) {
    prevFrom[symbolInfo.name] = null;
  }
  if (oldestDate.getTime() >= To.getTime()) {
    return null;
  }
  if (prevFrom[symbolInfo.name] && prevFrom[symbolInfo.name] <= To.getTime()) {
    To = new Date(prevFrom[symbolInfo.name]);
  }
  if (
    isByMinute &&
    moment(From)
      .add(7, 'day')
      .toDate()
      .getTime() > To.getTime()
  ) {
    From = moment(To)
      .subtract(7, 'day')
      .toDate();
  }
  if (
    isByHour &&
    moment(From)
      .add(6, 'month')
      .toDate()
      .getTime() > To.getTime()
  ) {
    From = moment(To)
      .subtract(6, 'month')
      .toDate();
  }
  if (
    !isByHour &&
    !isByMinute &&
    moment(From)
      .add(1, 'year')
      .toDate()
      .getTime() > To.getTime()
  ) {
    From = moment(To)
      .subtract(1, 'year')
      .toDate();
  }
  if (
    oldestDate.getTime() > From.getTime() &&
    oldestDate.getTime() < To.getTime()
  ) {
    From = oldestDate;
  }
  prevFrom[symbolInfo.name] = From.getTime();
  if (!latestTo[symbolInfo.name]) {
    latestTo[symbolInfo.name] = To.getTime();
  }
  if (latestTo[symbolInfo.name] < To.getTime()) {
    return null;
  }
  if (
    (isByMinute &&
      moment()
        .subtract(6, 'month')
        .toDate()
        .getTime() >= To.getTime()) ||
    (isByHour &&
      moment()
        .subtract(5, 'year')
        .toDate()
        .getTime() >= To.getTime())
  ) {
    return null;
  }
  return { From, To };
};

const getNextUnixRealTime = date => {
  const hours = moment(date).hour();
  const minutes = moment(date).minutes();
  if (hours >= 14 && minutes > 45) {
    return moment(date)
      .add(1, 'day')
      .set('hour', 9)
      .set('minute', 0)
      .startOf('minute')
      .unix();
  } else if (
    !((hours <= 11 && minutes <= 30) || (hours >= 13 && minutes >= 0))
  ) {
    return moment(date)
      .set('hour', 13)
      .set('minute', 0)
      .startOf('minute')
      .unix();
  } else {
    return moment(date)
      .startOf('minute')
      .unix();
  }
};

const getDataByMinutes = ({ data, resolution, from, to }) => {
  const minutes =
    (moment(to)
      .startOf('minute')
      .unix() -
      moment(from)
        .startOf('minute')
        .unix()) /
    60;
  const dataByKey = data.reduce(
    (obj, item) => ({ ...obj, [item.time]: item }),
    {},
  );
  const fullData = [];
  if (!data.length) return data;
  let prevTime = getNextUnixRealTime(new Date(data[0].time));
  for (let i = 0; i < minutes; i++) {
    if (
      moment(new Date(data[data.length - 1].time))
        .startOf('minute')
        .unix() < prevTime
    )
      break;
    if (dataByKey[prevTime * 1000]) {
      fullData.push(dataByKey[prevTime * 1000]);
    } else {
      const prevData = fullData[fullData.length - 1];
      fullData.push({
        ...prevData,
        time: getBarTime(new Date(prevTime * 1000), resolution),
      });
    }
    prevTime = getNextUnixRealTime(new Date((prevTime + 60) * 1000)); // plus 1 minute = 60s
  }
  return fullData;
};

const getDataByHours = ({ data, resolution, from, to }) => {
  const hours =
    (moment(to)
      .startOf('hour')
      .unix() -
      moment(from)
        .startOf('hour')
        .unix()) /
    (60 * 60); // 60s * 60m = 1h
  const dataByKey = data.reduce(
    (obj, item) => ({
      ...obj,
      [moment(new Date(item.time))
        .startOf('hour')
        .unix() * 1000]: item,
    }),
    {},
  );
  if (!data.length) return data;
  const fullData = data;
  let prevTime = getNextUnixRealTime(new Date(data[data.length - 1].time));
  for (let i = 0; i < hours; i++) {
    if (
      moment(to)
        .startOf('hour')
        .unix() < prevTime
    )
      break;
    if (dataByKey[prevTime * 1000]) {
      fullData.push(dataByKey[prevTime * 1000]);
    } else {
      fullData.push(
        formatBar(resolution)({ tradingDate: new Date(prevTime * 1000) }),
      );
    }
    prevTime = getNextUnixRealTime(new Date((prevTime + 60 * 60) * 1000)); // plus 1 hour = 60s * 60m
  }
  return fullData;
};

const getFullFromToBars = ({ symbolInfo, data, resolution, from, to }) => {
  const isByMinute = ['1', '5', '15', '30'].includes(resolution);
  const isByHour = ['60', '120', '240'].includes(resolution);
  const { From, To } = getFromToParams({
    symbolInfo,
    resolution,
    from,
    to,
    isCurrent: true,
  });
  if (isByMinute) {
    return getDataByMinutes({ data, resolution, from: From, to: To });
  } else if (isByHour) {
    return getDataByHours({ data, resolution, from: From, to: To });
  } else {
    return data;
  }
};

// Get full bars base on datetime from -> to AND countBack if data.length < countBack OR data.length < to - from
export const getFullFromToAndCountBackBars = ({
  symbolInfo,
  data,
  resolution,
  from,
  to,
  countBack,
}) => {
  // Will uncomment later if need mockup full data for all symbol or indicators
  // const fullData = getFullFromToBars({
  //   symbolInfo,
  //   data,
  //   resolution,
  //   from,
  //   to,
  // });
  let moreBars = [];
  if (data.length < countBack) {
    const isDatValid = data.length > 0;
    const latestDate = isDatValid ? data[0].time : from * 1000;
    moreBars = new Array(countBack - data.length).fill(null).map(() =>
      formatBar(resolution)({
        tradingDate: new Date(latestDate),
        lowestPrice: isDatValid ? data[0].low : NaN,
        highestPrice: isDatValid ? data[0].high : NaN,
        openPrice: isDatValid ? data[0].open : NaN,
        closePrice: isDatValid ? data[0].close : NaN,
        totalMatchVolume: isDatValid ? data[0].volume : NaN,
      }),
    );
  }
  return [...moreBars, ...data];
  // return fullData.length
  //   ? [
  //       ...fullData.slice(0, fullData.length - 1),
  //       ...moreBars,
  //       ...fullData.slice(-1),
  //     ]
  //   : [...fullData, ...moreBars];
};
